import React, { useState, useEffect, useRef } from 'react';
import { Monitor } from 'lucide-react';
import { SlotMachine } from './components/SlotMachine';
import { PremiumStore } from './components/PremiumStore';
import { SuccessPage } from './components/SuccessPage';

function useIsMobileDevice() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => {
      const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
      const uaMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase());
      const narrow = window.innerWidth < 768;
      setIsMobile(uaMobile || narrow);
    };

    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
}

function MobileOnlyScreen() {
  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 text-center">
      <div className="max-w-md mx-auto">
        <div className="mb-8 flex justify-center">
          <div className="w-24 h-24 rounded-full border-2 border-yellow-500/40 flex items-center justify-center bg-yellow-500/5">
            <Monitor className="w-12 h-12 text-yellow-500" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-yellow-500 font-serif tracking-wide mb-4">
          Desktop Only
        </h1>
        <p className="text-gray-300 text-lg leading-relaxed mb-2">
          Ten to Spin is currently only available on desktop and laptop computers.
        </p>
        <p className="text-gray-400 text-base leading-relaxed">
          Please visit <span className="text-yellow-400 font-semibold">TenToSpin.com</span> from a computer to play the slot machine.
        </p>
      </div>
    </div>
  );
}

function App() {
  const isMobile = useIsMobileDevice();
  const [showAdInfo, setShowAdInfo] = useState(false);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [showPremiumStore, setShowPremiumStore] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [coinsRemaining, setCoinsRemaining] = useState(100);
  const adSpaceRef = useRef<HTMLDivElement>(null);
  const [briaWhiteBg, setBriaWhiteBg] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setBriaWhiteBg(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setShowSuccess(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAdIndex(prev => (prev + 1) % 3);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && adSpaceRef.current && !adSpaceRef.current.contains(event.target as Node)) {
        setShowAdInfo(false);
      }
    };

    if (showAdInfo) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdInfo, isMobile]);

  const ADS = [
    <a key="ledger" href="https://shop.ledger.com/pages/ledger-nano-s-plus/?r=d887e180e2a0" target="_blank" rel="noopener noreferrer" className="block w-[200px] flex items-center justify-center">
      <img width={160} height={600} src="https://affiliate.ledger.com/image/160/600/Default" alt="Ledger Nano S Plus" className="rounded-lg shadow-lg" />
    </a>,
    <div key="noir21" className="w-[200px] flex items-center justify-center">
      <a href="https://Noir21.com" target="_blank" rel="noopener noreferrer" className="rounded-lg shadow-lg overflow-hidden block">
        <img src="/IMG_7907.JPG" width="200" height="600" alt="Noir21" className="object-cover hover:opacity-90 transition-opacity" />
      </a>
    </div>,
    <div key="bria" className="w-[200px] flex items-center justify-center">
      <a href="https://briabackwoods.com" target="_blank" rel="noopener noreferrer" className="rounded-lg shadow-lg overflow-hidden block">
        <img src="/bb.jpeg" width="200" height="600" alt="Bria Backwoods" className="object-cover hover:opacity-90 transition-opacity" />
      </a>
    </div>,
  ];

  const adIndex = currentAdIndex % ADS.length;

  const handleBackToHome = () => {
    setShowSuccess(false);
  };

  if (isMobile) {
    return <MobileOnlyScreen />;
  }

  if (showSuccess) {
    return <SuccessPage onBackToHome={handleBackToHome} />;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col relative">
      <main className="flex-grow flex items-center justify-center relative py-4 md:py-0">
        {showAdInfo && (
          <div ref={adSpaceRef} className="absolute -top-24 left-1/2 transform -translate-x-1/2 w-[728px] h-[90px] border-2 border-dashed border-yellow-500/50 rounded-lg flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="text-center">
              <p className="text-yellow-500 text-lg font-medium">Premium Ad Space</p>
              <p className="text-white text-sm">728x90 Leaderboard</p>
              <div className="flex items-center justify-center gap-4 mt-1">
                <p className="text-yellow-500 text-sm font-bold">$99 / month</p>
                <span className="text-gray-500">or</span>
                <p className="text-yellow-500 text-sm font-bold">$999 / year</p>
              </div>
            </div>
          </div>
        )}

        <div className="w-full max-w-[1600px] mx-auto px-2 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_800px_1fr] gap-8 relative">
            <div className="hidden md:flex justify-center items-center">
              {ADS[adIndex]}
            </div>

            <div className="w-full">
              <SlotMachine
                showStubHubAd={false}
                onSpin={() => {}}
                onCoinsChange={setCoinsRemaining}
              />
            </div>

            <div className="hidden md:flex justify-center items-center">
              {ADS[adIndex]}
            </div>
          </div>
        </div>
      </main>

      <div className="hidden md:flex justify-center bg-black/30 py-2 md:py-4 px-2">
        <a
          href="https://briabackwoods.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center rounded-lg shadow-lg hover:shadow-xl transition-all duration-700"
          style={{width: '100%', maxWidth: '660px', minHeight: '120px', height: '150px', backgroundColor: briaWhiteBg ? '#ffffff' : '#000000'}}
        >
          <span className="text-2xl md:text-3xl font-bold transition-colors duration-700" style={{color: briaWhiteBg ? '#000000' : '#ffffff'}}>BriaBackwoods.com</span>
        </a>
      </div>

      <div className="text-center py-3 px-3 md:py-4 md:px-4 text-gray-400 text-[10px] leading-[1.3] sm:text-xs sm:leading-tight max-w-6xl mx-auto">
        <p>
          <strong>Disclaimer:</strong> TenToSpin.com is an entertainment gaming website only. All games are for amusement and recreational purposes; no real money is awarded. This site does not offer or facilitate real-money gambling or wagering of any kind. Participation is intended for users aged 18 and older. Please play responsibly.
        </p>
      </div>

      <div className="text-center py-3 md:py-4 text-white text-sm">
        <p>Think you can beat the dealer? <a href="https://Noir21.com" target="_blank" rel="noopener noreferrer" className="text-yellow-500 hover:text-yellow-400 transition-colors font-semibold animate-pulse">Noir21.com</a></p>
      </div>

      <footer className="text-center py-3 md:py-6 text-gray-400 text-sm">
        <p>© <a href="https://SharperCollective.com" target="_blank" rel="noopener noreferrer" className="text-yellow-500 hover:text-yellow-400 transition-colors">Sharper Collective</a></p>
      </footer>

      {showPremiumStore && (
        <PremiumStore onClose={() => setShowPremiumStore(false)} />
      )}
    </div>
  );
}

export default App;
